import React, { useState } from 'react';
import SEO from '../components/SEO';
import Icon from '../components/Icon';
import PageHero from '../components/PageHero';
import services from '../data/services';
import './Contact.css';

const EMAIL = 'eatsleepcodereapeat@gmail.com';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: ''
};

const steps = [
  { title: 'We reply', description: 'A real engineer reads your message and replies with questions or next steps.' },
  { title: 'Discovery call', description: 'A short call to understand goals, constraints and timeline.' },
  { title: 'Proposal', description: 'A clear scope, plan and estimate, with no surprises later.' },
];

const Contact = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [interests, setInterests] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const toggleInterest = (name) => {
    setInterests((current) =>
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Using mailto as fallback, but you can integrate with EmailJS, Formspree, or your backend
      const mailtoLink = `mailto:${EMAIL}?subject=${encodeURIComponent(formData.subject || 'Contact from ESCR LLC Website')}&body=${encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone || 'Not provided'}\nInterested in: ${interests.length ? interests.join(', ') : 'Not specified'}\n\nMessage:\n${formData.message}`
      )}`;

      window.location.href = mailtoLink;

      setStatus({
        type: 'success',
        message: `Your email client should open. If not, please email us directly at ${EMAIL}`
      });

      setFormData(emptyForm);
      setInterests([]);
    } catch (error) {
      setStatus({
        type: 'error',
        message: `There was an error. Please email us directly at ${EMAIL}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    mainEntity: {
      '@type': 'Organization',
      name: 'ESCR LLC',
      email: EMAIL,
      url: 'https://escr.online'
    }
  };

  return (
    <div className="contact-page">
      <SEO
        title="Contact Us"
        description="Get in touch with ESCR LLC. We're here to help transform your business with innovative software solutions. Contact us today to discuss your project."
        keywords="contact ESCR LLC, software development contact, web development services, get in touch"
        structuredData={structuredData}
      />

      <PageHero
        eyebrow="Contact"
        title={
          <>
            Let's build something <span className="text-gradient">great.</span>
          </>
        }
        subtitle="Have a project in mind? Tell us about it and we'll respond as soon as possible."
      />

      <section className="section section-tight">
        <div className="container contact-layout">
          <aside className="contact-aside">
            <div className="contact-channels">
              <a href={`mailto:${EMAIL}`} className="card contact-channel">
                <span className="icon-tile">
                  <Icon name="mail" />
                </span>
                <span className="contact-channel-body">
                  <span className="contact-channel-label">Email</span>
                  <span className="contact-channel-value">{EMAIL}</span>
                </span>
                <Icon name="arrowUpRight" className="contact-channel-arrow" />
              </a>
              <a href="tel:+381606500893" className="card contact-channel">
                <span className="icon-tile">
                  <Icon name="phone" />
                </span>
                <span className="contact-channel-body">
                  <span className="contact-channel-label">Phone</span>
                  <span className="contact-channel-value">+381 60 6500 893</span>
                </span>
                <Icon name="arrowUpRight" className="contact-channel-arrow" />
              </a>
            </div>

            <div className="contact-steps">
              <h2 className="contact-steps-title">What happens next</h2>
              <ol>
                {steps.map((step, i) => (
                  <li key={step.title}>
                    <span className="contact-step-num">{i + 1}</span>
                    <span>
                      <span className="contact-step-title">{step.title}</span>
                      <span className="contact-step-desc">{step.description}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          <form className="contact-form" onSubmit={handleSubmit} aria-label="Contact form">
            <fieldset className="form-group">
              <legend className="form-label">What can we help with?</legend>
              <div className="interest-list">
                {services.map((service) => {
                  const selected = interests.includes(service.name);
                  return (
                    <button
                      type="button"
                      key={service.slug}
                      className={`interest ${selected ? 'is-selected' : ''}`}
                      aria-pressed={selected}
                      onClick={() => toggleInterest(service.name)}
                    >
                      <Icon name={selected ? 'check' : service.icon} />
                      {service.name}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Name <span className="required" aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-input"
                  placeholder="Jane Doe"
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email <span className="required" aria-hidden="true">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  placeholder="jane@company.com"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone <span className="optional">optional</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="form-input"
                  placeholder="+1 555 000 0000"
                  autoComplete="tel"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="subject" className="form-label">
                  Subject <span className="required" aria-hidden="true">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="form-input"
                  placeholder="New web platform"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Message <span className="required" aria-hidden="true">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                className="form-input form-textarea"
                rows="6"
                placeholder="Tell us about your project, goals and timeline…"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>

            {status.message && (
              <div className={`form-status ${status.type}`} role="alert">
                <Icon name={status.type === 'success' ? 'check' : 'close'} />
                {status.message}
              </div>
            )}

            <div className="form-footer">
              <p className="form-note">We'll only use your details to reply to you.</p>
              <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
                {isSubmitting ? 'Sending…' : 'Send message'}
                <Icon name="arrowRight" />
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
